import {
  Map as MapboxMap,
  NavigationControl,
  ScaleControl,
  Popup,
  GeolocateControl,
} from 'mapbox-gl';
import {
  STYLE,
  TOKEN,
  MAP_COLORS,
  TILESET,
  SOURCE,
  SOURCE_LAYER,
  GEOGRAPHIES,
} from './maputils/constants';
import { geocoder } from './maputils/geocoder';
import { renderTooltip } from './maputils/tooltip';
import { findElementByIdInShadowDom } from './utils/render.js';
import { json } from 'd3-fetch';

export async function renderPrecinctMap(
  container,
  geography,
  { boundaryUrl, resultsUrl, labelsUrl }
) {
  // results file: passed in by each map's entry script
  const results = new Map(Object.entries(await json(resultsUrl)));

  // boundary file: passed in by each map's entry script (see laCityMap.js)

  // labels file (optional): a GeoJSON of points with a `name` property.
  // Only maps that pass one get labels (e.g. neighborhoods on the city map).

  // Load both files at the same time
  const [boundaryGeoJSON, labelsGeoJSON] = await Promise.all([
    json(boundaryUrl),
    labelsUrl ? json(labelsUrl) : null,
  ]);

  // Tooltip data = tile properties (precinct, neighborhood, city...) + results, if any
  const tooltipData = (feature) => ({
    ...feature.properties,
    ...results.get(feature.id),
  });

  // candidates + colors
  const candidates = JSON.parse(
    findElementByIdInShadowDom('candidates').textContent
  ).map((c) => c.value);

  const popup = new Popup({
    closeButton: false,
    closeOnClick: false,
    offset: 8,
  });

  const geo = GEOGRAPHIES[geography];
  const padding = window.innerWidth < 600 ? 40 : 20;

  const map = new MapboxMap({
    accessToken: TOKEN,
    container,
    style: STYLE,
    // bounds: geo.BOUNDS,
    // fitBoundsOptions: { padding: padding },
    maxZoom: geo.MAX_ZOOM,
    cooperativeGestures: true,
  });

  // Disable rotation, allow pan/zoom
  map.dragRotate.disable();
  map.touchZoomRotate.disableRotation();

  // Lock the starting view as the limit: the zoom that fits the whole
  // region is as far out as readers can go, and the visible area is as
  // far as they can pan.
  function lockToRegion() {
    map.setMaxBounds(null);
    map.setMinZoom(null);
    map.fitBounds(geo.BOUNDS, { padding, duration: 0 });
    map.setPadding({ top: 0, bottom: 0, left: 0, right: 0 });
    map.setMinZoom(map.getZoom());
    map.setMaxBounds(map.getBounds());
  }
  lockToRegion();

  // If the container changes size (e.g. a phone rotates), re-fit and re-lock
  map.on('resize', lockToRegion);

  map.addControl(new NavigationControl({ showCompass: false }));
  // map.addControl(new ScaleControl({ unit: 'imperial' }), 'bottom-right');

  // Add geolocate control to the map.
  // map.addControl(
  //   new GeolocateControl({
  //     positionOptions: {
  //       enableHighAccuracy: true,
  //     },
  //     // When active the map will receive updates to the device's location as it changes.
  //     trackUserLocation: false,
  //     // Draw an arrow next to the location dot to indicate which direction the device is heading.
  //     showUserHeading: false,
  //   })
  // );

  await map.once('load');

  // --- Precinct vector source ---
  map.addSource(SOURCE, {
    type: 'vector',
    url: TILESET,
    promoteId: { [SOURCE_LAYER]: 'precinct' },
  });

  // Attach each precinct's results to its shape on the map.
  // Paint expressions below read these with ['feature-state', ...].
  const candidatesByName = new Map(candidates.map((c) => [c.name, c]));
  const colorFor = (row) => {
    if (row.winner === 'tie') return MAP_COLORS.tie;
    const c = candidatesByName.get(row.winner); // a Map built from meta.aml's list
    return c?.colors[row.winner_color - 1]; // undefined if unknown winner
  };

  for (const [precinct, row] of results) {
    map.setFeatureState(
      { source: SOURCE, sourceLayer: SOURCE_LAYER, id: precinct },
      {
        winner: row.winner,
        winner_color: row.winner_color,
        color: colorFor(row),
        // TODO: the CSV has no opacity yet (it's computed from vote density
        // in 03-la-create-mapbox-data). Default to full strength for now.
        opacity: row.opacity ?? 1,
        lowVotes: (row.total_votes ?? 0) <= 10,
      }
    );
  }

  // Which precincts this map shows
  // Which precincts this map shows (set per geography in constants.js).
  // The county map has no filter, so the key is left off entirely.
  const geographyFilter = geo.FILTER ? { filter: geo.FILTER } : {};

  // Styled results layer
  map.addLayer(
    {
      'id': 'results-data',
      'type': 'fill',
      'source': SOURCE,
      'source-layer': SOURCE_LAYER,
      ...geographyFilter,
      'paint': {
        'fill-color': [
          'to-color',
          ['coalesce', ['feature-state', 'color'], MAP_COLORS.noData],
        ],
        'fill-opacity': [
          '*',
          0.8,
          ['coalesce', ['feature-state', 'opacity'], 1],
        ],
        // 'fill-outline-color': [
        //   'match',
        //   ['coalesce', ['feature-state', 'winner'], 'none'],
        //   ['Karen Ruth Bass'],
        //   'rgb(231, 197, 87)',
        //   ['Nithya Raman'],
        //   'rgb(160, 230, 161)',
        //   'rgba(0, 0, 0, 0)',
        // ],
      },
    },
    'road-minor-link'
  );

  // Hatch low votes, and precincts with no results at all. The county leaves
  // some vote-by-mail-only precincts out of its file entirely, so they never
  // get a lowVotes state; the `true` fallback hatches them too.
  map.addLayer(
    {
      'id': 'low-votes-hatch',
      'type': 'fill',
      'source': SOURCE,
      'source-layer': SOURCE_LAYER,
      ...geographyFilter,
      'paint': {
        'fill-pattern': 'pattern-7-01',
        'fill-opacity': [
          'case',
          ['boolean', ['feature-state', 'lowVotes'], true],
          0.8,
          0,
        ],
      },
    },
    'road-minor-link'
  );

  // Border layer — highlights on hover via feature state
  map.addLayer({
    'id': 'precinct-line',
    'type': 'line',
    'source': SOURCE,
    'source-layer': SOURCE_LAYER,
    ...geographyFilter,
    'paint': {
      'line-width': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        1.5,
        1,
      ],
      'line-color': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        '#000',
        '#fff',
      ],
      'line-opacity': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        1,
        0.2,
      ],
    },
  });

  // Invisible fill on top for hit detection
  map.addLayer({
    'id': 'precinct-hover',
    'type': 'fill',
    'source': SOURCE,
    'source-layer': SOURCE_LAYER,
    ...geographyFilter,
    'paint': {
      'fill-opacity': 0,
    },
  });

  // --- city/county/CD boundary ---
  map.addSource('boundary', {
    type: 'geojson',
    data: boundaryGeoJSON,
  });

  map.addLayer({
    id: 'boundary-outline',
    type: 'line',
    source: 'boundary',
    paint: {
      'line-color': '#333333',
      'line-opacity': 1,
      'line-border-width': 0,
      'line-width': 1,
    },
    layout: {
      'line-miter-limit': 1.5,
    },
  });

  // --- Place labels (only if this map was given a labels file) ---
  if (labelsGeoJSON) {
    map.addSource('labels', {
      type: 'geojson',
      data: labelsGeoJSON,
    });

    map.addLayer({
      id: 'place-labels',
      type: 'symbol',
      source: 'labels',
      layout: {
        'text-field': ['get', 'name'],
        'text-font': ['BentonGothic Medium', 'Arial Unicode MS Regular'],
        'text-size': 11,
        'text-transform': 'uppercase',
        'text-letter-spacing': 0.08,
        'text-max-width': 7,
        'text-padding': 10,
        'text-justify': 'left',
      },
      paint: {
        'text-color': 'rgb(80, 80, 80)',
        'text-halo-color': 'rgba(246, 245, 245, 0.9)',
        'text-halo-width': 1.5,
      },
    });
  }

  /** @type {string | number | undefined} */
  let hoveredId;
  /** @type {string | number | undefined} */
  let selectedId;

  function setFeatureHover(id, value) {
    map.setFeatureState(
      { source: SOURCE, sourceLayer: SOURCE_LAYER, id },
      { hover: value }
    );
  }

  function selectFeature(id, props, lngLat) {
    if (selectedId !== undefined) setFeatureHover(selectedId, false);
    setFeatureHover(id, true);
    selectedId = id;
    if (props && lngLat) {
      popup.setLngLat(lngLat).setHTML(renderTooltip(props)).addTo(map);
    }
  }

  map.on('mousemove', 'precinct-hover', (e) => {
    if (e.features.length > 0) {
      const feature = e.features[0];
      if (hoveredId !== undefined && hoveredId !== selectedId) {
        setFeatureHover(hoveredId, false);
      }
      hoveredId = e.features[0].id;
      if (hoveredId !== selectedId) {
        setFeatureHover(hoveredId, true);
      }
      if (selectedId === undefined) {
        popup
          .setLngLat(e.lngLat)
          .setHTML(renderTooltip(tooltipData(feature)))
          .addTo(map);
      }
      map.getCanvas().style.cursor = 'pointer';
    }
  });

  map.on('mouseleave', 'precinct-hover', () => {
    if (hoveredId !== null && hoveredId !== selectedId) {
      setFeatureHover(hoveredId, false);
    }
    hoveredId = undefined;
    map.getCanvas().style.cursor = '';
    if (selectedId === undefined) popup.remove();
  });

  function closePopup() {
    if (selectedId !== undefined) {
      setFeatureHover(selectedId, false);
      selectedId = undefined;
    }
    popup.remove();
  }

  let flyingTo = false;
  map.on('dragstart', (e) => {
    if (e.originalEvent) closePopup();
  });
  map.on('zoomstart', () => {
    if (!flyingTo) closePopup();
  });

  map.on('click', 'precinct-hover', (e) => {
    if (e.features.length > 0) {
      const feature = e.features[0];
      selectFeature(feature.id, tooltipData(feature), e.lngLat);
      flyingTo = true;
      map.once('moveend', () => {
        flyingTo = false;
      });
      map.flyTo({ center: e.lngLat, zoom: Math.max(map.getZoom(), 11.99) });
    }
  });

  findElementByIdInShadowDom('geocoder').appendChild(geocoder.onAdd(map));

  geocoder.on('result', (e) => {
    map.once('moveend', () => {
      const point = map.project(e.result.center);
      const features = map.queryRenderedFeatures(point, {
        layers: ['precinct-hover'],
      });
      if (features.length > 0) {
        const feature = features[0];
        selectFeature(feature.id, tooltipData(feature), e.result.center);
      }
    });
  });

  return map;
}
